import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { softDeleteFile } from './fileDeletionApi';
import type { BaseComponent } from '../types/protocols';

export class BuildingImagesApi {
    private db = getDatabase();

    /**
     * Verify that the current user is an admin
     */
    private async verifyAdmin(): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }

        const adminRef = ref(this.db, `users/userList/${user.uid}/admin`);
        const adminSnapshot = await get(adminRef);
        if (!adminSnapshot.exists() || adminSnapshot.val() !== true) {
            throw new Error('Du måste vara administratör för att ta bort bilder');
        }
    }

    /**
     * Root-relative removals of every `photoLocations/{photoKey}` pin that sits on
     * the photo being deleted, so no component keeps a marker on a photo that is
     * gone. Pins are keyed by the photo's push key, so this is a key lookup on
     * every component of the building (archived ones included).
     */
    private async photoLocationRemovals(buildingKey: string, photoKey: string): Promise<Record<string, null>> {
        const snapshot = await get(ref(this.db, `buildings/${buildingKey}/structure/components`));
        const components = (snapshot.val() ?? {}) as Record<string, Partial<BaseComponent> | null>;
        const removals: Record<string, null> = {};
        for (const [componentKey, component] of Object.entries(components)) {
            if (component?.photoLocations && photoKey in component.photoLocations) {
                removals[`buildings/${buildingKey}/structure/components/${componentKey}/photoLocations/${photoKey}`] = null;
            }
        }
        return removals;
    }

    /**
     * Delete a photo from a component (soft delete — tombstones the metadata).
     * Pins other components carry on this photo are removed in the same write.
     */
    async deleteComponentPhoto(
        buildingKey: string,
        componentKey: string,
        photoKey: string
    ): Promise<void> {
        await this.verifyAdmin();

        await softDeleteFile({
            originalPath: `buildings/${buildingKey}/structure/components/${componentKey}/photos/${photoKey}`,
            entityType: 'componentPhoto',
            extraUpdates: await this.photoLocationRemovals(buildingKey, photoKey),
        });
    }

    /**
     * Delete a photo from a room (soft delete — tombstones the metadata)
     */
    async deleteRoomPhoto(
        buildingKey: string,
        roomKey: string,
        photoKey: string
    ): Promise<void> {
        await this.verifyAdmin();

        await softDeleteFile({
            originalPath: `buildings/${buildingKey}/structure/rooms/${roomKey}/photos/${photoKey}`,
            entityType: 'roomPhoto',
        });
    }
}

// Export singleton instance
export const buildingImagesApi = new BuildingImagesApi();
